import { CURRENCY } from "./constants"
import { SentryWrapper } from "./ThirdParty/Sentry/SentryWrapper"
import { IS_PRODUCTION, SURVEY_URL } from "./env-management"
import { isBrowser } from "./infrastructure/ssg-utils"

export function toggle<T>(ids: T[], id: T) {
    const index = ids.findIndex((value) => value === id)
    if (index === -1) return [...ids, id]
    return ids.filter((_, idsIndex) => idsIndex !== index)
}

export function padStart(str: string, targetLength: number, padString: string) {
    str = String(str)
    targetLength = targetLength >> 0
    padString = String(typeof padString !== "undefined" ? padString : " ")
    if (str.length >= targetLength) {
        return String(str)
    } else {
        targetLength = targetLength - str.length
        if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length)
        }
        return padString.slice(0, targetLength) + String(str)
    }
}

export function formatPrice(price: number, currency: CURRENCY) {
    if (!isBrowser()) return price.toString()
    const locale =
        navigator.languages.find(
            (language) => Intl.NumberFormat.supportedLocalesOf(language).length > 0
        ) || "Ru-ru"
    const priceString = Intl.NumberFormat(locale, {
        useGrouping: Math.trunc(price).toString().length > 4,
    })
        .format(price)
        .replace("-", "\u2212")

    return formatCurrencySymbol(priceString, currency)
}

export function formatNumber(n: number | undefined, options?: Intl.NumberFormatOptions) {
    if (n === undefined) return n
    return n.toLocaleString("Ru-ru", options)
}

export function formatCurrencySymbol(price: string, currency: CURRENCY = CURRENCY.RUB) {
    return {
        RUB: `${price} ₽`,
        USD: `$${price}`,
    }[currency]
}

export function formatLocalDateIso(date: Date) {
    return (
        date.getFullYear().toString() +
        "-" +
        padStart((date.getMonth() + 1).toString(), 2, "0") +
        "-" +
        padStart(date.getDate().toString(), 2, "0")
    )
}

export function formatUtcDateIso(date: Date) {
    return (
        date.getUTCFullYear().toString() +
        "-" +
        padStart((date.getUTCMonth() + 1).toString(), 2, "0") +
        "-" +
        padStart(date.getUTCDate().toString(), 2, "0")
    )
}

export function formatUtcTimeIso(date: Date) {
    return (
        padStart(date.getUTCHours().toString(), 2, "0") +
        ":" +
        padStart(date.getUTCMinutes().toString(), 2, "0") +
        ":00"
    )
}

export function formatLocalDate(date: Date) {
    return new Intl.DateTimeFormat("ru-ru").format(date)
}

export function formatSessionNumber(n: number) {
    const data = [
        "нулевую",
        "первую",
        "вторую",
        "третью",
        "четвертую",
        "пятую",
        "шестую",
        "седьмую",
        "восьмую",
        "девятую",
        "десятую",
    ]
    if (!data[n]) return n.toString()
    return data[n]
}

export function formatLocalTime(date: Date) {
    try {
        return new Intl.DateTimeFormat("ru-ru", { hour: "numeric", minute: "numeric" }).format(date)
    } catch (e) {
        if (e instanceof RangeError) {
            SentryWrapper.getInstance().captureException(e, {
                extra: { date },
            })
        }
        return date.toString()
    }
}

export function formatLocalTimeWithTimezone(date: Date) {
    return formatLocalTime(date) + " (" + localTimezoneGmtString() + ")"
}

export function localTimezoneGmtString() {
    const offset = new Date().getTimezoneOffset()
    const hours = Math.abs(Math.floor(offset / 60))
    const minutes = Math.abs(offset % 60)
    let result = "GMT" + (offset > 0 ? "-" : "+") + hours.toString()
    if (minutes !== 0) {
        result += ":" + minutes.toString()
    }
    return result
}

// This is a dirty hack.
// Shift datetime so as to local time representation matches that in GMT+03:00 timezone.
// For example: original datetime is 2020-01-27 10:00:00 GTM+03:00
// If local TZ is GMT-06:00 we want 2020-01-27 10:00:00 GMT-06:00
export function localDatetimeAsMoscow(original_dt: Date) {
    const dt = new Date(original_dt)
    return new Date(dt.getTime() + dt.getTimezoneOffset() * 60000 + 3 * 60 * 60000)
}

export function loadScript(
    src: string,
    onLoad?: (e: Event) => void | Promise<unknown>,
    onError?: () => void
) {
    const script = document.createElement("script")

    script.async = true
    script.src = src
    if (onLoad) {
        script.onload = onLoad
    }

    script.onerror = () => {
        SentryWrapper.getInstance().captureException(`Failed to load script: ${src}`, {})
        if (onError) {
            onError()
        }
    }
    script.onabort = () => undefined

    return document.body.appendChild(script)
}

function isCrossOriginFrame() {
    if (!isBrowser()) return
    if (!window.top) {
        return true
    }
    try {
        return !window.top?.location.hostname
    } catch {
        return true
    }
}

function isSameOriginFrame() {
    if (!isBrowser()) return
    return window.self !== window.top
}

export function getRootPath() {
    if (isCrossOriginFrame() || isSameOriginFrame()) return document.location.href
    return IS_PRODUCTION ? "/" : SURVEY_URL
}

export function hasProp<T extends object, K extends PropertyKey>(
    obj: T,
    key: K
): obj is T & Record<K, never> {
    return key in obj
}

export function isObject(x: unknown): x is object {
    return typeof x === "object" && x !== null
}

export function unique<T>(arr: T[]): T[] {
    return Array.from(new Set(arr))
}

export function arrayToQueryString(key: string, values: string[]) {
    const searchParams = new URLSearchParams()
    for (const value of values) {
        searchParams.append(key, value)
    }
    return searchParams.toString()
}

export function capitalize(str: string): string {
    return str.slice(0, 1).toUpperCase() + str.slice(1)
}

export function isEmptyObject(obj: object) {
    return Object.keys(obj).length === 0 && obj.constructor === Object
}

export function allMonths() {
    return [...Array(12).keys()].map((key) =>
        new Date(0, key).toLocaleString("ru", { month: "long" })
    )
}

export function monthsNameInDativeCase(month: number): string | undefined {
    const data = [
        "январе",
        "феврале",
        "марте",
        "апреле",
        "мае",
        "июне",
        "июле",
        "августе",
        "сентябре",
        "октябре",
        "ноябре",
        "декабре",
    ]
    return data[month]
}

export function getTimezoneString() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone.replace("/", ", ")
}

export async function downloadFile(url: string, filename: string) {
    try {
        const response = await fetch(url)
        if (!response.ok) throw new Error(`Failed to download: ${response.status}`)
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = objectUrl
        a.download = filename
        a.click()
        URL.revokeObjectURL(objectUrl)
    } catch (e) {
        SentryWrapper.getInstance().captureException(e, {})
    }
}
